import { z } from 'zod';

export interface CommandNode {
  type: 'root' | 'literal' | 'argument';
  children?: Record<string, CommandNode>;
  parser?: string;
  properties?: Record<string, unknown>;
  redirect?: string[];
  executable?: boolean;
}
export const CommandNodeSchema: z.ZodType<CommandNode> = z.object({
  type: z.enum(['root', 'literal', 'argument']),
  children: z
    .record(
      z.string(),
      z.lazy(() => CommandNodeSchema),
    )
    .optional(),
  parser: z.string().optional(),
  properties: z.record(z.string(), z.unknown()).optional(),
  redirect: z.array(z.string()).optional(),
  executable: z.boolean().optional(),
});
export const CommandCatalogSchema = z.object({
  tree: CommandNodeSchema.nullable(),
  commands: z.array(z.string().regex(/^[a-zA-Z0-9_:.-]+$/)).max(10000),
});
export type CommandCatalog = z.infer<typeof CommandCatalogSchema>;
export const CompletionQuerySchema = z.object({ input: z.string().max(4096).default('') }).strict();
export const CompletionsSchema = z.object({
  start: z.number().int().nonnegative(),
  candidates: z.array(z.object({ value: z.string(), description: z.string() })).max(50),
});
export type Completions = z.infer<typeof CompletionsSchema>;
/** 補助プラグインの返事。null は「補助プラグインがない・動いていない」 */
export const LiveCompletionsSchema = z.object({ values: z.array(z.string().max(4096)).max(2000) }).nullable();
export type LiveCompletions = z.infer<typeof LiveCompletionsSchema>;
/** サーバーの補完（最後の語の候補）を、パネルの形（置き換える位置と候補）にする */
export function liveToCompletions(input: string, values: string[]): Completions {
  const start = input.length - (input.match(/\S*$/)?.[0].length ?? 0);
  const prefix = input.slice(start);
  const root = !/\s/.test(input.replace(/^\//, '').trimStart());
  const seen = new Set<string>();
  const candidates = values
    // 1 語目はサーバーが名前の一致で絞り込んだ一覧。名前空間つき（minecraft:say など）は打ち始めたときだけ出す
    .filter((value) => !root || prefix.includes(':') || !value.includes(':'))
    .filter((value) => !seen.has(value) && seen.add(value))
    .sort((a, b) => (root ? a.localeCompare(b) : 0))
    .slice(0, 50)
    .map((value) => ({ value, description: '' }));
  // サーバーは最後の語の「どこから」置き換えるかを返さない（例: @e[type=minecraft:zo → minecraft:zombie）。
  // 候補の先頭と一致する、最後の語のいちばん長い末尾から置き換える
  const first = candidates[0]?.value;
  let offset = 0;
  if (first !== undefined) {
    while (offset < prefix.length && !first.startsWith(prefix.slice(offset))) offset++;
  }
  const from = start + offset;
  return { start: root && input.startsWith('/') ? Math.max(from, 1) : from, candidates };
}

function redirected(node: CommandNode, root: CommandNode): CommandNode {
  // The vanilla report omits the redirect array when its target is the root.
  if (
    !node.children &&
    (node === root.children?.execute?.children?.run || node === root.children?.return?.children?.run)
  )
    return root;
  if (!node.redirect) return node;
  let target = root;
  for (const name of node.redirect) {
    const next = target.children?.[name];
    if (!next) return node;
    target = next;
  }
  return target;
}
function values(node: CommandNode, players: string[]): string[] {
  switch (node.parser) {
    case 'minecraft:game_profile':
    case 'minecraft:entity':
      return players;
    case 'minecraft:dimension':
      return ['minecraft:overworld', 'minecraft:the_nether', 'minecraft:the_end'];
    case 'brigadier:bool':
      return ['true', 'false'];
    case 'minecraft:gamemode':
      return ['survival', 'creative', 'adventure', 'spectator'];
    case 'minecraft:difficulty':
      return ['peaceful', 'easy', 'normal', 'hard'];
    default:
      return [];
  }
}
function width(node: CommandNode) {
  if (['minecraft:vec3', 'minecraft:block_pos'].includes(node.parser ?? '')) return 3;
  if (['minecraft:vec2', 'minecraft:column_pos', 'minecraft:rotation'].includes(node.parser ?? '')) return 2;
  return 1;
}
function accepts(node: CommandNode, token: string) {
  if (node.parser === 'brigadier:bool') return ['true', 'false'].includes(token);
  if (['brigadier:integer', 'brigadier:long', 'brigadier:float', 'brigadier:double'].includes(node.parser ?? ''))
    return /^-?\d+(?:\.\d+)?$/.test(token);
  return true;
}
function walk(
  node: CommandNode,
  root: CommandNode,
  tokens: string[],
  index: number,
  budget: { left: number },
): CommandNode[] {
  if (--budget.left < 0) return [];
  node = redirected(node, root);
  if (index === tokens.length) return [node];
  const entries = Object.entries(node.children ?? {});
  const literal = entries.find(([name, child]) => child.type === 'literal' && name === tokens[index]);
  if (literal) return walk(literal[1], root, tokens, index + 1, budget);
  return entries.flatMap(([, child]) => {
    if (child.type !== 'argument' || !accepts(child, tokens[index]!)) return [];
    if (child.properties?.type === 'greedy') return [];
    const next = index + width(child);
    return next <= tokens.length ? walk(child, root, tokens, next, budget) : [];
  });
}
/** The cursor is at the end. Unknown/greedy arguments are deliberately not guessed. */
export function consoleCompletions(catalog: CommandCatalog, input: string, players: string[] = []): Completions {
  CompletionQuerySchema.parse({ input });
  const offset = input.startsWith('/') ? 1 : 0;
  const text = input.slice(offset);
  const parts = [...text.matchAll(/"(?:\\.|[^"\\])*"|[^\s]+/g)];
  const last = /\s$/.test(text) ? undefined : parts.pop();
  const prefix = last?.[0] ?? '';
  const start = offset + (last?.index ?? text.length);
  const tokens = parts.map((part) => part[0]);
  const found = new Map<string, string>();
  const add = (value: string, description: string) => {
    if (value.startsWith(prefix)) found.set(value, description);
  };
  if (!tokens.length) for (const command of catalog.commands) add(command, 'プラグイン・Mod コマンド');
  if (catalog.tree) {
    for (const node of walk(catalog.tree, catalog.tree, tokens, 0, { left: 10000 })) {
      if (node === catalog.tree) for (const command of catalog.commands) add(command, 'プラグイン・Mod コマンド');
      for (const [name, child] of Object.entries(node.children ?? {})) {
        if (child.type === 'literal') add(name, 'コマンド・サブコマンド');
        else for (const value of values(child, players)) add(value, `${name} (${child.parser})`);
      }
    }
  }
  return {
    start,
    candidates: [...found]
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(0, 50)
      .map(([value, description]) => ({ value, description })),
  };
}
